import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X, Lock, Maximize2, Minimize2 } from "lucide-react";
import { readerApi, paymentsApi, publishingApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve relative /api/... paths to full URL with credentials; keep external URLs as-is
const resolvePageSrc = (src) => {
  if (!src) return src;
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  return `${BACKEND_URL}${src}`;
};

const PaywallScreen = ({ book, pricing, onBuy, purchasing }) => (
  <div data-testid="paywall" className="min-h-screen bg-primary flex items-center justify-center px-6 py-24">
    <div className="max-w-2xl w-full text-center">
      <Lock className="w-8 h-8 text-jcrown-gold mx-auto mb-6" />
      <p className="text-jcrown-gold/70 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
        Keep Reading
      </p>
      <h2 className="font-heading text-white text-4xl md:text-6xl tracking-tighter uppercase mb-6">
        You've Reached
        <br />
        <span className="text-white/40">The End of Preview</span>
      </h2>
      <p className="text-white/50 font-body text-base md:text-lg mb-12 max-w-lg mx-auto">
        Unlock the complete digital edition of <span className="text-white">{book.title}</span> to continue.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <button
          onClick={() => onBuy(book.stripe_lookup_key || `individual_${book.slug}`)}
          disabled={purchasing}
          data-testid="paywall-buy-individual"
          className="border-2 border-jcrown-gold bg-black/40 p-6 hover:bg-jcrown-gold hover:text-jcrown-dark transition-all group text-left disabled:opacity-50"
        >
          <p className="text-jcrown-gold group-hover:text-jcrown-dark text-[10px] tracking-widest uppercase mb-2">Own This Book</p>
          <p className="font-heading text-white group-hover:text-jcrown-dark text-3xl tracking-tight uppercase mb-1">${pricing.individual_book}</p>
          <p className="text-white/40 group-hover:text-jcrown-dark/60 font-body text-xs">Digital edition</p>
        </button>
        <button
          onClick={() => onBuy("complete_collection")}
          disabled={purchasing}
          data-testid="paywall-buy-collection"
          className="border border-white/20 bg-black/40 p-6 hover:border-white transition-all text-left disabled:opacity-50"
        >
          <p className="text-white/40 text-[10px] tracking-widest uppercase mb-2">All 6 Books</p>
          <p className="font-heading text-white text-3xl tracking-tight uppercase mb-1">${pricing.complete_collection}</p>
          <p className="text-white/40 font-body text-xs">Save ${pricing.collection_savings}</p>
        </button>
        <button
          onClick={() => onBuy("library_pass_monthly")}
          disabled={purchasing}
          data-testid="paywall-buy-pass"
          className="border border-white/20 bg-black/40 p-6 hover:border-white transition-all text-left disabled:opacity-50"
        >
          <p className="text-white/40 text-[10px] tracking-widest uppercase mb-2">Library Pass</p>
          <p className="font-heading text-white text-3xl tracking-tight uppercase mb-1">${pricing.library_pass_monthly}<span className="text-lg text-white/50">/mo</span></p>
          <p className="text-white/40 font-body text-xs">Cancel anytime</p>
        </button>
      </div>

      <Link
        to={`/books/${book.slug}`}
        data-testid="paywall-back"
        className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors font-body text-xs tracking-widest uppercase"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to Book Details
      </Link>
    </div>
  </div>
);

export default function Reader() {
  const { slug } = useParams();
  const { user, authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [page, setPage] = useState(0);
  const [purchasing, setPurchasing] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [book, setBook] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (authLoading) return; // wait for auth to resolve
    
    let cancelled = false;
    setNotFound(false);
    setData(null);
    setPage(0);
    setReachedEnd(false);
    
    const loadContent = async () => {
      // Try full pages first if authenticated
      if (user) {
        try {
          const full = await readerApi.fullPages(slug);
          if (cancelled) return;
          setData(full);
          setBook({ slug, title: full.title, stripe_lookup_key: `individual_${slug}` });
          return;
        } catch (e) {
          if (cancelled) return;
          if (e.response?.status === 404) {
            setNotFound(true);
            return;
          }
          // 401/403 → fall through to preview
        }
      }
      try {
        const preview = await readerApi.preview(slug);
        if (cancelled) return;
        setData(preview);
        setBook({ slug, title: preview.title, stripe_lookup_key: `individual_${slug}` });
      } catch (e) {
        if (cancelled) return;
        setNotFound(true);
      }
    };
    loadContent();
    publishingApi.listBooks().then(d => { if (!cancelled) setPricing(d.pricing); }).catch(() => {});
    return () => { cancelled = true; };
  }, [slug, user, authLoading]);

  const next = () => {
    if (!data) return;
    if (page < data.pages.length - 1) {
      setPage(page + 1);
    } else if (data.is_preview) {
      setReachedEnd(true);
    }
  };
  const prev = () => setPage(Math.max(0, page - 1));

  const buy = async (lookup_key) => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/read/${slug}`)}`);
      return;
    }
    setPurchasing(true);
    try {
      const { checkout_url } = await paymentsApi.checkout(lookup_key);
      window.location.href = checkout_url;
    } catch (e) {
      setPurchasing(false);
      alert("Checkout failed.");
    }
  };

  if (notFound) {
    return (
      <div className="bg-primary min-h-screen flex items-center justify-center text-center px-6">
        <div>
          <h1 className="font-heading text-white text-5xl uppercase mb-4">Not Found</h1>
          <p className="text-white/50 font-body mb-8">This book isn't in the library.</p>
          <Link to="/books" data-testid="reader-notfound-back" className="text-jcrown-gold underline">← Back to Library</Link>
        </div>
      </div>
    );
  }

  if (!data || !pricing || !book || authLoading) {
    return (
      <div className="bg-primary min-h-screen flex items-center justify-center">
        <div className="text-white/30 font-body text-sm tracking-widest uppercase">Loading...</div>
      </div>
    );
  }

  if (reachedEnd) {
    return <PaywallScreen book={book} pricing={pricing} onBuy={buy} purchasing={purchasing} />;
  }

  const currentPage = data.pages[page];
  const totalPages = data.pages.length;
  const progress = ((page + 1) / totalPages) * 100;

  return (
    <div data-testid="reader" className={`bg-primary min-h-screen flex flex-col ${fullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Top Bar */}
      <div className="border-b border-white/5 bg-primary/90 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            to="/my-library"
            data-testid="reader-close"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors font-body text-xs tracking-widest uppercase"
          >
            <X className="w-4 h-4" />
            Close
          </Link>
          <div className="flex-1 max-w-md">
            <p className="text-white font-heading text-sm tracking-tight uppercase text-center truncate">{data.title}</p>
            {data.is_preview && (
              <p className="text-jcrown-gold/70 text-[9px] tracking-widest uppercase text-center mt-1">Free Preview</p>
            )}
          </div>
          <button
            onClick={() => setFullscreen(!fullscreen)}
            data-testid="reader-fullscreen"
            className="text-white/50 hover:text-white transition-colors hidden md:block"
          >
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
        <div className="h-0.5 bg-white/5">
          <div className="h-full bg-jcrown-gold transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Page */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12 relative">
        {/* Desktop side arrows */}
        <button
          onClick={prev}
          disabled={page === 0}
          data-testid="reader-prev"
          className="hidden md:flex absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/60 border border-white/10 text-white/70 hover:text-white hover:bg-black/80 transition-all items-center justify-center disabled:opacity-20 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="max-w-3xl w-full">
          <img
            src={resolvePageSrc(currentPage)}
            alt={`Page ${page + 1}`}
            className="w-full h-auto max-h-[75vh] object-contain mx-auto shadow-2xl"
          />
        </div>
        <button
          onClick={next}
          data-testid="reader-next"
          className="hidden md:flex absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/60 border border-white/10 text-white/70 hover:text-white hover:bg-black/80 transition-all items-center justify-center"
        >
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/5 py-4">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between gap-4">
          {/* Mobile prev button */}
          <button
            onClick={prev}
            disabled={page === 0}
            data-testid="reader-prev-mobile"
            className="md:hidden w-10 h-10 rounded-full bg-black/60 border border-white/10 text-white/70 hover:text-white transition-all flex items-center justify-center disabled:opacity-20"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 flex items-center justify-center gap-4">
            <span data-testid="reader-page" className="text-white/40 font-body text-xs tracking-widest uppercase">
              Page {page + 1} of {totalPages}
            </span>
            {data.is_preview && page === totalPages - 1 && (
              <button
                onClick={() => setReachedEnd(true)}
                data-testid="reader-end-preview"
                className="hidden md:inline-flex items-center gap-2 text-jcrown-gold hover:text-white transition-colors font-body text-xs tracking-widest uppercase"
              >
                Unlock Book →
              </button>
            )}
          </div>
          {/* Mobile next button */}
          <button
            onClick={next}
            data-testid="reader-next-mobile"
            className="md:hidden w-10 h-10 rounded-full bg-jcrown-gold/90 text-jcrown-dark hover:bg-white transition-all flex items-center justify-center"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
